package com.kinoshazam.screens.main.compact

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import com.kinoshazam.screens.main.continueWatching.ContinueHideMode
import com.kinoshazam.screens.main.continueWatching.ContinueWatchingSection
import com.kinoshazam.uiKit.components.videoItem.compactBannerSkeletons
import com.kinoshazam.uiKit.components.cells.cellItem.CellItem
import com.kinoshazam.uiKit.components.cells.cellItem.CellItemState
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.videoItem.CompactVideoItem
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.UiKitTheme
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.arrow_right
import kinoshazam.resources.generated.resources.films
import kinoshazam.resources.generated.resources.films_soon
import kinoshazam.resources.generated.resources.films_watched
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.soon_in_cinema
import kinoshazam.resources.generated.resources.tv_shows
import kinoshazam.resources.generated.resources.tv_shows_watched
import kinoshazam.resources.generated.resources.watch_all
import kinoshazam.resources.generated.resources.which_watch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.allVideos.AllVideosState.ScreenType.NewMovies
import presentation.screens.allVideos.AllVideosState.ScreenType.NewTvShows
import presentation.screens.allVideos.AllVideosState.ScreenType.ReleaseSoon
import presentation.screens.main.MainMVIAction
import presentation.screens.main.MainState

@Composable
fun MainContent(
    paddingValues: PaddingValues,
    state: MainState,
    onAction: (MainMVIAction) -> Unit,
) {
    LazyColumn(
        modifier = Modifier.padding(paddingValues)
    ) {
        if (state.watchVideo && (!state.continueWatchingReady || state.continueWatching.isNotEmpty())) {
            item {
                ContinueWatchingSection(
                    items = state.continueWatching,
                    wide = false,
                    hideMode = ContinueHideMode.Sheet,
                    loading = !state.continueWatchingReady,
                    onOpen = { onAction(MainMVIAction.OpenContinue(it)) },
                    onHide = { onAction(MainMVIAction.HideContinue(it)) },
                )
            }
        }

        item {
            CellItem(
                state = CellItemState(
                    title = TextLabelState(
                        text = buildAnnotatedString {
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                    fontWeight = BoldMedium.fontWeight
                                )
                            ) {
                                append("${stringResource(Res.string.films)},")
                            }
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                )
                            ) {
                                append(" ${stringResource(Res.string.soon_in_cinema)}")
                            }
                        }
                    )
                )
            )

            val films = stringResource(Res.string.films_soon)
            val soon = state.soonMovies
            LazyRow(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                contentPadding = PaddingValues(start = 16.dp)
            ) {
                if (soon == null) {
                    compactBannerSkeletons()
                } else {
                itemsIndexed(soon) { index, it ->
                    CompactVideoItem(
                        state = VideoState(
                            id = it.id ?: -1,
                            title = it.title.orEmpty(),
                            image = it.imageUrl,
                            placeHolder = painterResource(Res.drawable.movie),
                            placeHolderColor = UiKitTheme.colors.color_5,
                            onAction = { id ->
                                onAction(MainMVIAction.OnDetailsClicked(id))
                            }
                        ),
                    )
                }
                item {
                    CompactVideoItem(
                        state = VideoState(
                            id = 1,
                            title = stringResource(Res.string.watch_all),
                            placeHolder = painterResource(Res.drawable.arrow_right),
                            placeHolderColor = UiKitTheme.colors.color_2,
                            onAction = {
                                onAction(
                                    MainMVIAction.OnAllVideoClicked(
                                        screenType = ReleaseSoon,
                                        title = films,
                                    )
                                )
                            }
                        ),
                        modifier = Modifier.padding(end = 16.dp)
                    )
                }
                }
            }
        }

        item {
            CellItem(
                state = CellItemState(
                    title = TextLabelState(
                        text = buildAnnotatedString {
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                    fontWeight = BoldMedium.fontWeight
                                )
                            ) {
                                append("${stringResource(Res.string.films)},")
                            }
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                )
                            ) {
                                append(" ${stringResource(Res.string.which_watch)}")
                            }
                        }
                    )
                )
            )

            val films = stringResource(Res.string.films_watched)
            val movies = state.newMovies
            LazyRow(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                if (movies == null) {
                    compactBannerSkeletons()
                } else {
                itemsIndexed(movies) { index, it ->
                    CompactVideoItem(
                        state = VideoState(
                            id = it.id ?: -1,
                            title = it.title.orEmpty(),
                            image = it.imageUrl,
                            placeHolder = painterResource(Res.drawable.movie),
                            placeHolderColor = UiKitTheme.colors.color_5,
                            onAction = { id ->
                                onAction(MainMVIAction.OnDetailsClicked(id))
                            }
                        ),
                        modifier = when (index) {
                            0 -> Modifier.padding(start = 16.dp)
                            else -> Modifier.padding()
                        }
                    )
                }
                item {
                    CompactVideoItem(
                        state = VideoState(
                            id = 1,
                            title = stringResource(Res.string.watch_all),
                            placeHolder = painterResource(Res.drawable.arrow_right),
                            placeHolderColor = UiKitTheme.colors.color_2,
                            onAction = {
                                onAction(
                                    MainMVIAction.OnAllVideoClicked(
                                        screenType = NewMovies,
                                        title = films,
                                    )
                                )
                            }
                        ),
                        modifier = Modifier.padding(end = 16.dp)
                    )
                }
                }
            }
        }

        item {
            CellItem(
                state = CellItemState(
                    title = TextLabelState(
                        text = buildAnnotatedString {
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                    fontWeight = BoldMedium.fontWeight
                                )
                            ) {
                                append("${stringResource(Res.string.tv_shows)},")
                            }
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                )
                            ) {
                                append(" ${stringResource(Res.string.which_watch)}")
                            }
                        }
                    )
                )
            )

            val tvShows = stringResource(Res.string.tv_shows_watched)
            val shows = state.newTvShows
            LazyRow(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                if (shows == null) {
                    compactBannerSkeletons()
                } else {
                itemsIndexed(shows) { index, it ->
                    CompactVideoItem(
                        state = VideoState(
                            id = it.id ?: -1,
                            title = it.title.orEmpty(),
                            image = it.imageUrl,
                            placeHolder = painterResource(Res.drawable.movie),
                            placeHolderColor = UiKitTheme.colors.color_5,
                            onAction = { id ->
                                onAction(MainMVIAction.OnTvShowClicked(id))
                            }
                        ),
                        modifier = when (index) {
                            0 -> Modifier.padding(start = 16.dp)
                            else -> Modifier.padding()
                        }
                    )
                }
                item {
                    CompactVideoItem(
                        state = VideoState(
                            id = 1,
                            title = stringResource(Res.string.watch_all),
                            placeHolder = painterResource(Res.drawable.arrow_right),
                            placeHolderColor = UiKitTheme.colors.color_2,
                            onAction = {
                                onAction(
                                    MainMVIAction.OnAllVideoClicked(
                                        screenType = NewTvShows,
                                        title = tvShows,
                                    )
                                )
                            }
                        ),
                        modifier = Modifier.padding(end = 16.dp)
                    )
                }
                }
            }
            Spacer(modifier = Modifier.navigationBarsPadding().padding(bottom = 16.dp))
        }
    }
}